import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ErrorIcon } from 'assets/icons/toast-error.svg';
import ValuesModal from 'components/ValuesModal/ValuesModal';
import errorMessagesFormatted from 'utils/formattedErrorText';

import type { IErrorMessages } from 'store/slices/dataSets/interfaces/IErrorItem';
import type { IImportMultipleErrorsItemProps } from '../interfaces/IImportMultipleErrorsItemProps';

import styles from '../scss/ImportMultipleErrorsModal.module.scss';

function ImportMultipleErrorsItem({ sheetKey, sheetErrors }: IImportMultipleErrorsItemProps) {
  const { t } = useTranslation();
  const currentSheetErrors = sheetErrors[sheetKey];
  const [activeModalId, setActiveModalId] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const handleShowMoreClick = (modalId: string) => {
    setActiveModalId((prevId) => (prevId === modalId ? null : modalId));
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
      setActiveModalId(null);
    }
  };

  useEffect(() => {
    if (activeModalId) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeModalId]);

  return (
    <div className={styles.errorItemList}>
      <div className={styles.errorItemListTitle}>
        <span>{`${sheetKey}.`}</span>
        <span>{t('dataSetsManagement.importErrorsModal.validationErrors')}</span>
      </div>
      {Object.entries(currentSheetErrors).map(([itemError, errorData]) => (
        <div
          key={itemError}
          className={styles.errorItemWrapperListContent}
        >
          <span>
            <b>{t('dataSetsManagement.importErrorsModal.errorRow')}</b>
            {' '}
            {itemError}
          </span>
          {Object.entries(errorData).map(([itemErrorColumn, errorMessages]) => (
            <div
              key={itemErrorColumn}
              className={styles.errorItemListContent}
            >
              <div className={styles.errorLeftWrapper}>
                <span>
                  <b>{t('dataSetsManagement.importErrorsModal.errorColumn')}</b>
                  {' '}
                  {itemErrorColumn}
                </span>
              </div>
              <div className={styles.errorRightWrapper}>
                <ErrorIcon />
                <div className={styles.errorList}>
                  {errorMessages.map((errorMessage: IErrorMessages, errorIndex: number) => {
                    const modalId = `${sheetKey}-${itemError}-${itemErrorColumn}-${errorIndex}`;
                    return (
                      <span key={errorMessage.error} className={styles.errorItem}>
                        {errorMessagesFormatted(errorMessage.error, errorMessage.params)}
                        {errorMessage.params.values ? (
                          <div ref={activeModalId === modalId ? modalRef : null}>
                            <ValuesModal
                              isVisible={activeModalId === modalId}
                              values={errorMessage.params.values}
                              onClick={() => handleShowMoreClick(modalId)}
                            />
                          </div>
                        ) : null}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export default ImportMultipleErrorsItem;
